// ===============================
// 📌 FundStatus.js (FINAL VERSION)
// ===============================

import React, { useState, useEffect } from "react";
import { ResponsiveBar } from "@nivo/bar";
import { ResponsivePie } from "@nivo/pie";
import axios from "axios";
import { API_BASE } from "../config";

export default function FundStatus() {
  const [energyData, setEnergyData] = useState([]);
  const [pieData, setPieData] = useState([]);
  const [districts, setDistricts] = useState([]);

  // ---------------------- DEFAULT FY ----------------------
  const getCurrentFY = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1;
    return month < 4 ? `${year - 1}-${year}` : `${year}-${year + 1}`;
  };

  const currentFY = getCurrentFY();

  // ---------------------- FILTER STATES ----------------------
  const [selectedDistrict, setSelectedDistrict] = useState("0");
  const [selectedFY, setSelectedFY] = useState(currentFY);

  // Applied filters (used in heading)
  const [appliedDistrict, setAppliedDistrict] = useState("0");
  const [appliedFY, setAppliedFY] = useState(currentFY);

  // FY → Start & End Dates
  const getFYDates = (fy) => {
    const [startYear, endYear] = fy.split("-");
    return {
      fy_start: `${startYear}-04-01`,
      fy_end: `${endYear}-03-31`,
    };
  };

  // FY Dropdown list
  const getFYList = () => {
    const [start, end] = currentFY.split("-").map(Number);
    return [
      `${start - 1}-${end - 1}`,
      `${start}-${end}`,
      `${start + 1}-${end + 1}`,
    ];
  };

  const financialYears = getFYList();

  // ---------------------- LOAD DISTRICT LIST ----------------------
  useEffect(() => {
    axios.get(`${API_BASE}/districts`).then((res) => setDistricts(res.data));
  }, []);

  // --------------------------------------------------------
  // 🚀 FETCH FUND DATA
  // --------------------------------------------------------
  const fetchFundStatus = async (dist = "0", fy = currentFY) => {
    try {
      const fyObj = getFYDates(fy);
  
      const res = await axios.get(`${API_BASE}/fund-allocation-summary`, {
        params: {
          dist,
          fy_start: fyObj.fy_start,
          fy_end: fyObj.fy_end
        },
      });
  

      const barChartData = res.data.map((row) => ({
        name: row.district_name,
        district_id: row.dist_id,
        count: Number(row.total_amount),
      }));

      const pieChartData = res.data.map((row) => ({
        id: row.dist_id,
        label: row.district_name,
        value: Number(row.total_amount),
      }));

      setEnergyData(barChartData);
      setPieData(pieChartData);
    } catch (err) {
      console.error("Error loading fund data:", err);
      setEnergyData([]);
      setPieData([]);
    }
  };

  // ---------------------- INITIAL LOAD (Always current FY + All districts) ----------------------
  useEffect(() => {
    fetchFundStatus("0", currentFY);
  }, []);

  // ---------------------- HEADING LABELS ----------------------
  const appliedDistrictName =
    appliedDistrict === "0"
      ? "All"
      : districts.find((d) => String(d.dist_id) === appliedDistrict)?.district ||
        "All";

  const appliedFYLabel = appliedFY;

  const chartHeading = (title) => (
    <span>
      {title} (
      District: <span style={{ color: "green", fontWeight: 600 }}>{appliedDistrictName}</span>
      {" | "} FY:{" "}
      <span style={{ color: "green", fontWeight: 600 }}>{appliedFYLabel}</span>)
    </span>
  );

  // ---------------------- CHART SETTINGS ----------------------
  const barChartSettings = {
    keys: ["count"],
    indexBy: "name",
    margin: { top: 80, right: 50, bottom: 160, left: 80 },
    padding: 0.35,
    borderRadius: 6,
    colors: (bar) => ["#4CAF50", "#FF9800", "#03A9F4", "#9C27B0"][bar.index % 4],
    axisBottom: {
      tickRotation: -35,
      tickSize: 5,
      tickPadding: 15,
      legend: "Districts",
      legendPosition: "middle",
      legendOffset: 100,
    },
    axisLeft: {
      legend: "Fund Allocation Amount (₹)",
      legendPosition: "middle",
      legendOffset: -60,
    },
    legends: [
      {
        dataFrom: "keys",
        anchor: "bottom",
        direction: "row",
        translateY: 80,
        itemsSpacing: 12,
        itemWidth: 140,
        itemHeight: 20,
        symbolSize: 18,
      },
    ],
  };

  const pieChartSettings = {
    margin: { top: 40, right: 20, bottom: 40, left: 20 },
    innerRadius: 0.5,
    padAngle: 0.7,
    cornerRadius: 4,
    activeOuterRadiusOffset: 6,
    colors: { scheme: "set2" },
  };

  // =============================== UI ===============================
  return (
    <div style={{ padding: "30px", background: "#f4f6f8", minHeight: "100vh" }}>
      <h2 style={{ marginTop: 0 }}>Fund Allocation Status</h2>

      {/* FILTERS */}
      <div
        style={{
          display: "flex",
          gap: "20px",
          flexWrap: "wrap",
          marginBottom: "20px",
        }}
      >
        {/* District Filter */}
        <label>
          District:
          <select
            value={selectedDistrict}
            onChange={(e) => setSelectedDistrict(e.target.value)}
            style={{ marginLeft: 10, padding: 5 }}
          >
            <option value="0">-- All --</option>
            {districts.map((d) => (
              <option key={d.dist_id} value={d.dist_id}>
                {d.district}
              </option>
            ))}
          </select>
        </label>

        {/* FY Filter */}
        <label>
          Financial Year:
          <select
            value={selectedFY}
            onChange={(e) => setSelectedFY(e.target.value)}
            style={{ marginLeft: 10, padding: 5 }}
          >
            {financialYears.map((fy) => (
              <option key={fy} value={fy}>
                {fy}
              </option>
            ))}
          </select>
        </label>

        {/* Search Button */}
        <button
          style={{
            padding: "10px 25px",
            background: "#2196F3",
            color: "#fff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            marginTop: "-3px",
          }}
          onClick={() => {
            setAppliedDistrict(selectedDistrict);
            setAppliedFY(selectedFY);
            fetchFundStatus(selectedDistrict, selectedFY);
          }}
        >
          Search
        </button>
      </div>

      {/* BAR CHART */}
      <div
        style={{
          height: 450,
          background: "#fff",
          padding: 20,
          borderRadius: 12,
          marginBottom: 35,
        }}
      >
        <h3>{chartHeading("Bar Chart: Fund Allocation Distribution")}</h3>

        <ResponsiveBar
          data={energyData}
          {...barChartSettings}
          tooltip={({ data }) => (
            <div
              style={{
                padding: "6px 10px",
                background: "white",
                borderRadius: 6,
                boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              }}
            >
              <strong>{data.name}</strong>: ₹{data.count.toLocaleString()}
            </div>
          )}
          onClick={(bar) => {
            sessionStorage.setItem("fundDetailsFilters", JSON.stringify({
              district: bar.data.district_id,
              fy: appliedFY
            }));
          
            window.open("/fund-details", "_blank");
          }}
          
          
        />
      </div>

      {/* PIE CHART */}
      <div
        style={{
          height: 450,
          background: "#fff",
          padding: "20px",
          borderRadius: 12,
        }}
      >
        <h3>{chartHeading("Pie Chart: Fund Allocation Distribution")}</h3>
        <ResponsivePie
  data={pieData}
  {...pieChartSettings}

  // ⭐ Inside slice → show amount (formatted)
  arcLabel={(d) => `₹${d.value.toLocaleString()}`}

  arcLabelsSkipAngle={10}
  arcLabelsTextColor="#000"

  // ⭐ Outside label → show district name
  arcLinkLabel={(d) => d.label}

  tooltip={({ datum }) => (
    <div
      style={{
        padding: "6px 10px",
        background: "white",
        borderRadius: 6,
        boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
      }}
    >
      <strong>{datum.label}</strong>: ₹
      {datum.value.toLocaleString()}
    </div>
  )}

  onClick={(slice) => {
    sessionStorage.setItem("fundDetailsFilters", JSON.stringify({
      district: slice.data.id,
      fy: appliedFY
    }));
  
    window.open("/fund-details", "_blank");
  }}
  
  
/>


      </div>
    </div>
  );
}
